import { Input, Label } from 'components/atoms';
import Button from 'components/atoms/button/Button';
import {
  FormInput,
  InfoBox,
  LabeledStatus,
  TabsNavigation,
} from 'components/molecules';
import { TopBar } from 'components/organisms';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiOpenAiLogo } from 'react-icons/pi';
import { useGetIsrc } from 'services/codesDistributor/isrcGenerator';
import {
  useGetGenres,
  useGetLanguages,
  useGetPublisherLightList,
  getGenreLabel,
} from 'services/enums';
import { ARTIST_ROLE, STATUS } from 'services/types';
import { IVideoMutationForm, TRACK_CENSORIOUS } from 'services/video/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import {
  getYearsSelectOptions,
  useEnumToSelectOptions,
} from 'utils/components/inputs/select';
import { getLanguageSelectOptions } from 'utils/global/language.utils';
import {
  conditionalField,
  credensialsValidation,
  externalIdsValidation,
  requiredEnumSelect,
  titlesValidation,
} from 'utils/global/validation.utils';
import { array, number, object, string } from 'yup';
import {
  artistsValidation,
  settlementInfoContractValidation,
} from '../music-product-digital/MusicProductDigitalForm';
import { IFormikProps, IFormProps } from '../types';
import VideoArtists from './tabs/VideoArtists';
import VideoContracts from './tabs/VideoContracts';
import VideoContractsFuture from './tabs/VideoContractsFuture';
import VideoContractsHistory from './tabs/VideoContractsHistory';
import VideoExternal from './tabs/VideoExternal';
import VideoFile from './tabs/VideoFile';
import VideoSubtitles from './tabs/VideoSubtitles';
import VideoTitles from './tabs/VideoTitles';

export enum VideoTabs {
  file,
  artists,
  titles,
  subtitles,
  external,
  contracts,
}

export const videoInitialValues: IVideoMutationForm = {
  censorious: TRACK_CENSORIOUS.unknown,
  genre: { id: -1, name: '' },
  language: -1,
  // source: TRACK_SOURCE.unknown,
  status: STATUS.draft,
  mainTitle: {
    language: -1,
    title: '',
    version: '',
  },
  cline: { name: '', year: '' },
  pline: { name: '', year: '' },
  isrc: '',
  otherTitles: [],
  artists: [],
  isInstrumental: true,
  isCreatedWithAi: false,
  subtitles: [],
  externalIds: [],
  canUpdateISRC: true,
  hasDuplicatedArtist: null,
  contracts: [],
  version: 0,
};

export const videoValidation = object().shape({
  status: requiredEnumSelect(STATUS),
  mainTitle: titlesValidation,
  language: number()
    .not([-1], () => i18next.t('formik-error.required'))
    .required(() => i18next.t('formik-error.required')),
  censorious: conditionalField(requiredEnumSelect(TRACK_CENSORIOUS)),
  genre: conditionalField(
    object().shape({
      id: number()
        .not([-1], () => i18next.t('formik-error.required'))
        .required(() => i18next.t('formik-error.required')),
    })
  ),
  // source: conditionalField(requiredEnumSelect(TRACK_SOURCE)),
  isrc: conditionalField(
    string().required(() => i18next.t('formik-error.required'))
  ),
  pline: credensialsValidation('status'),
  cline: credensialsValidation('status'),
  otherTitles: array().of(titlesValidation),
  externalIds: array().of(externalIdsValidation),
  contracts: array().of(settlementInfoContractValidation),
  artists: conditionalField(
    array()
      .of(artistsValidation)
      .min(1, () => i18next.t('formik-error.at-least-one-artist-required'))
      .test(
        'has-main-artist',
        () =>
          i18next.t('formik-error.what-should-have-main-artist', {
            what: i18next.t('video.what'),
          }),
        artist =>
          artist && artist.some(item => item.role === ARTIST_ROLE.mainArtist)
      )
      .test(
        'has-composer',
        () =>
          i18next.t('formik-error.what-should-have-composer', {
            what: i18next.t('video.what'),
          }),
        artist =>
          artist && artist.some(item => item.role === ARTIST_ROLE.composer)
      ),
    'status',
    [-1, 1],
    array().of(artistsValidation)
  ),
  file: conditionalField(
    string().when('originalVideoLink', {
      is: (link?: string) => link === undefined,
      then: schema => schema.required(() => i18next.t('formik-error.required')),
      otherwise: schema => schema.optional(),
    })
  ),
  originalVideoLink: conditionalField(
    string().when('file', {
      is: (file?: File) => file === undefined,
      then: schema => schema.required(() => i18next.t('formik-error.required')),
      otherwise: schema => schema.optional(),
    })
  ),
});

export interface IVideoForm
  extends FormikConfig<IVideoMutationForm>,
    IFormProps,
    II18nextPrefix {}

const VideoForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = videoInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  ...props
}: IVideoForm) => {
  const { t } = useTranslation();
  const { getSelectOptions, getStatusOptions } = useEnumToSelectOptions();
  const getIsrc = useGetIsrc();

  const languages = useGetLanguages();

  const publishers = useGetPublisherLightList();

  const genres = useGetGenres();

  const fetchIsrc = async (
    setFieldValue: (field: string, value: any) => void
  ) => {
    const result = await getIsrc.refetch();

    if (result?.data) {
      setFieldValue('isrc', result?.data?.value);
    }
  };

  const boolOptions = [
    { value: true, label: t(`enum.LABEL_REPAYMENTS.Yes`) },
    { value: false, label: t(`enum.LABEL_REPAYMENTS.No`) },
  ];

  return (
    <Formik
      {...props}
      validationSchema={videoValidation}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}>
      {({
        values,
        setFieldValue,
        status,
        errors,
      }: IFormikProps<IVideoMutationForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status?.displayMode ? tittle : undefined}
              path={backPath ?? 'video'}
              topbarContent={
                <div className='flex w-full justify-end'>
                  {status?.displayMode ? null : (
                    <Input.ComplexWrapper
                      sizes={[50, 50]}
                      className='max-w-[500px]'>
                      <FormInput.Text
                        name='mainTitle.title'
                        label={`${t('input.label.title')}`}
                        autoFocus
                      />
                      <FormInput.Text
                        name='mainTitle.version'
                        label={`${t('input.label.version')}`}
                      />
                    </Input.ComplexWrapper>
                  )}
                  <div className='flex items-center gap-2 justify-end w-full'>
                    {status?.displayMode ? (
                      values.isCreatedWithAi && (
                        <Label color='blue' size='compact'>
                          <PiOpenAiLogo /> {t('input.label.isCreatedWithAi')}
                        </Label>
                      )
                    ) : (
                      <div className='w-48'>
                        <FormInput.Select
                          options={boolOptions}
                          label={t('input.label.isCreatedWithAi')}
                          name='isCreatedWithAi'
                          onChange={option => {
                            setFieldValue(
                              'isCreatedWithAi',
                              option.value ? true : false
                            );

                            setFieldValue('contracts', []);
                          }}
                        />
                      </div>
                    )}
                    {status?.displayMode ? (
                      <LabeledStatus status={initialValues?.status} />
                    ) : (
                      <div className='w-48'>
                        <FormInput.Select
                          name='status'
                          label={t('input.label.status')}
                          options={getStatusOptions(
                            STATUS,
                            initialValues?.status,
                            'enum.STATUS'
                          )}
                        />
                      </div>
                    )}
                    <div>{topbarContent}</div>
                  </div>
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Items columns={2}>
                  <FormInput.Select
                    name='language'
                    label={t('input.label.language')}
                    options={getLanguageSelectOptions(languages.data, [
                      ...values.otherTitles,
                      values.mainTitle,
                    ])}
                    value={{
                      value: values.mainTitle.language,
                      label: t(`enum.LANGUAGE.${values.mainTitle.language}`),
                    }}
                    onChange={({ value }) =>
                      setFieldValue('mainTitle.language', value)
                    }
                  />

                  <Input.ComplexWrapper
                    sizes={
                      !status?.displayMode &&
                      !(initialValues.isrc || values.isrc)
                        ? [69.5, 30]
                        : []
                    }
                    className='w-full'>
                    <FormInput.Text
                      name='isrc'
                      label='ISRC'
                      placeholder='USP031900001'
                      disabled={values.canUpdateISRC === false}
                    />

                    {!status?.displayMode &&
                      !(initialValues.isrc || values.isrc) && (
                        <Button
                          variant={BUTTON_VARIANT.default}
                          onClick={() => fetchIsrc(setFieldValue)}>
                          <Button.Text text={t('button.generate')} />
                        </Button>
                      )}
                  </Input.ComplexWrapper>

                  <FormInput.Select
                    name='censorious'
                    label={t('input.label.censorious')}
                    options={getSelectOptions(
                      TRACK_CENSORIOUS,
                      'enum.TRACK_CENSORIOUS'
                    )}
                  />

                  <FormInput.Select
                    name='genre.id'
                    label={t('input.label.genre')}
                    options={
                      genres.data?.map(genre => ({
                        value: genre.id,
                        label: getGenreLabel(genre.id, t),
                      })) ?? []
                    }
                    isLoading={genres.isLoading}
                    value={{
                      value: values.genre.id,
                      label: getGenreLabel(values.genre.id, t),
                    }}
                    onChange={option => {
                      if (option) {
                        setFieldValue('genre', {
                          id: option.value,
                          name: getGenreLabel(option.value as number, t),
                        });
                      }
                    }}
                  />
                  <Input.ComplexWrapper sizes={[40, 60]} className='w-full'>
                    <FormInput.Select
                      name='pline.year'
                      label={`${t('input.label.year')}`}
                      placeholder=''
                      options={getYearsSelectOptions()}
                      isCreatable
                    />
                    <FormInput.Select
                      name='pline.name'
                      label={`${t('input.label.pline')}`}
                      placeholder=''
                      options={publishers.data}
                      isCreatable
                      titleEnabled
                    />
                  </Input.ComplexWrapper>
                  <Input.ComplexWrapper sizes={[40, 60]} className='w-full'>
                    <FormInput.Select
                      name='cline.year'
                      label={`${t('input.label.year')}`}
                      placeholder=''
                      options={getYearsSelectOptions()}
                      isCreatable
                    />
                    <FormInput.Select
                      name='cline.name'
                      label={`${t('input.label.cline')}`}
                      placeholder=''
                      options={publishers.data}
                      isCreatable
                      titleEnabled
                    />
                  </Input.ComplexWrapper>
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                tabs={[
                  {
                    value: VideoTabs.file,
                    name: 'file',
                    error: errors.file || errors.originalVideoLink,
                  },
                  {
                    value: VideoTabs.artists,
                    name: 'artists',
                    error:
                      errors.artists ||
                      (!!values.hasDuplicatedArtist
                        ? t('toast-message.duplicated-artist-error')
                        : undefined),
                  },

                  {
                    value: VideoTabs.titles,
                    name: 'titles',
                    error: errors.otherTitles,
                  },
                  {
                    value: VideoTabs.subtitles,
                    name: 'subtitles',
                    error: errors.subtitles,
                  },
                  {
                    value: VideoTabs.external,
                    name: 'external',
                    error: errors.externalIds,
                  },
                  {
                    value: VideoTabs.contracts,
                    name: 'contracts',
                    error: errors.contracts,
                    permissionName: 'ContractPermissionsLIST**',
                  },
                ]}
                i18nextPrefix={i18nextPrefix}
                disabled={disableTabs}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

VideoForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'file', element: <VideoFile /> },
    { path: 'artists', element: <VideoArtists /> },
    { path: 'titles', element: <VideoTitles /> },
    { path: 'subtitles', element: <VideoSubtitles /> },
    { path: 'external', element: <VideoExternal /> },
    { path: 'contracts', element: <VideoContracts /> },
    { path: 'contracts/future', element: <VideoContractsFuture /> },
    { path: 'contracts/history', element: <VideoContractsHistory /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default VideoForm;
